/**
 * Live level meters from the DSP kernel (tracks + master).
 * Host caches the latest snapshot; UI polls via getTelemetry() / rAF.
 */

export type TrackTelemetry = {
  trackId: number;
  rmsL: number;
  rmsR: number;
  peakL: number;
  peakR: number;
};

export type MasterTelemetry = {
  rmsL: number;
  rmsR: number;
  peakL: number;
  peakR: number;
};

export type EngineTelemetry = {
  tracks: TrackTelemetry[];
  master: MasterTelemetry;
};

/** How many slots are published in each telemetry snapshot. */
export const METER_SLOT_COUNT = 128;

/** Publish a new snapshot every N samples (~5.3ms @ 48k). */
export const METER_PUBLISH_SAMPLES = 256;

export type KernelTelemetryMessage = {
  type: "telemetry";
  /** Interleaved per track: rmsL, rmsR, peakL, peakR (length n*4). */
  n: number;
  data: Float32Array;
  /** rmsL, rmsR, peakL, peakR */
  master: Float32Array;
};

export function createEmptyTelemetry(trackCount = METER_SLOT_COUNT): EngineTelemetry {
  const n = Math.max(0, trackCount | 0);
  return {
    tracks: Array.from({ length: n }, (_, trackId) => ({
      trackId,
      rmsL: 0,
      rmsR: 0,
      peakL: 0,
      peakR: 0,
    })),
    master: { rmsL: 0, rmsR: 0, peakL: 0, peakR: 0 },
  };
}

/** Mutate `target` from a worklet telemetry message (avoids alloc in the rAF path). */
export function applyTelemetryMessage(
  target: EngineTelemetry,
  message: KernelTelemetryMessage,
): void {
  const n = Math.min(message.n, target.tracks.length);
  const data = message.data;
  for (let i = 0; i < n; i++) {
    const track = target.tracks[i]!;
    const o = i * 4;
    track.rmsL = data[o] ?? 0;
    track.rmsR = data[o + 1] ?? 0;
    track.peakL = data[o + 2] ?? 0;
    track.peakR = data[o + 3] ?? 0;
  }
  for (let i = n; i < target.tracks.length; i++) {
    const track = target.tracks[i]!;
    track.rmsL = 0;
    track.rmsR = 0;
    track.peakL = 0;
    track.peakR = 0;
  }
  const m = message.master;
  target.master.rmsL = m[0] ?? 0;
  target.master.rmsR = m[1] ?? 0;
  target.master.peakL = m[2] ?? 0;
  target.master.peakR = m[3] ?? 0;
}

/**
 * Map linear RMS to a 0..1 meter height (approx −60 dBFS floor).
 * Cheap enough for rAF over many cells.
 */
export function meterHeightFromRms(rms: number): number {
  if (!(rms > 0)) {
    return 0;
  }
  const db = 20 * Math.log10(rms);
  if (db <= -60) {
    return 0;
  }
  if (db >= 0) {
    return 1;
  }
  return (db + 60) / 60;
}
